/**
 * classification — fonctions pures de la classification de confidentialité (UPT-2039,
 * phase 0 : UPT-2043). Niveaux ordonnés C1 à C4, compartiments de besoin d'en connaître
 * (PII, HR, FIN, CRISE, SANTE, LEGAL), caviardage lu dans `_classification`.
 *
 * Règle d'or : le web ne décide JAMAIS de la visibilité. Le serveur caviarde ; le web
 * lit `_classification.redacted` dans les réponses et `clearance` dans le contexte
 * d'authentification. `canReadWithClearance` n'est qu'une aide à l'affichage anticipé
 * des badges (« vous ne verrez pas ce champ ») : elle ne masque rien et ne remplace
 * jamais la réponse du serveur.
 *
 * Les accesseurs de clés i18n sont ICI, au niveau module : la règle i18n refuse une clé
 * composée (`t(\`...${x}\`)`) dans le JSX.
 *
 * Aucun import de React ni du DOM : ce module est testé sous jest
 * (`classification.test.ts`).
 */

import type { AuthContextClearance } from '@/types/auth_context';

// ─── Niveaux et compartiments ────────────────────────────────────────────

/** C1 public, C2 interne (défaut de tout ce qui n'est pas classé), C3 confidentiel, C4 secret. */
export type ConfidentialityLevel = 'C1' | 'C2' | 'C3' | 'C4';

/** Les niveaux, du plus ouvert au plus restreint. */
export const CONFIDENTIALITY_LEVELS: readonly ConfidentialityLevel[] = ['C1', 'C2', 'C3', 'C4'];

/** Niveau de tout ce qui n'est pas classé, et habilitation d'un rôle sans ligne. */
export const DEFAULT_CONFIDENTIALITY_LEVEL: ConfidentialityLevel = 'C2';

/**
 * Plafond d'une clé d'API MCP : C4 est refusé par le serveur (422
 * `classification.keyCeilingC4Forbidden`), et le défaut est C2.
 */
export const MCP_KEY_LEVELS: readonly ConfidentialityLevel[] = ['C1', 'C2', 'C3'];
export const MCP_KEY_DEFAULT_LEVEL: ConfidentialityLevel = 'C2';

/** Vocabulaire fermé décidé le 04.10.2026 (le serveur le sert aussi : `GET /compartments`). */
export const COMPARTMENT_CODES: readonly string[] = ['PII', 'HR', 'FIN', 'CRISE', 'SANTE', 'LEGAL'];

/** Marque posée par le serveur à la place d'une valeur caviardée. */
export const REDACTED_MARK = '••••';

/** Habilitation du contexte d'authentification (`clearance`). */
export type Clearance = AuthContextClearance;

/** Classification EFFECTIVE d'un champ ou d'une entité (niveau + compartiments). */
export type ClassificationLike = {
  level?: string | null;
  compartments?: readonly string[] | null;
};

/** Ton d'état d'un niveau : jamais la marque du locataire. */
export type ClassificationTone = 'neutral' | 'warning' | 'danger';

const LEVEL_RANK: Record<ConfidentialityLevel, number> = { C1: 1, C2: 2, C3: 3, C4: 4 };

/** Lit un niveau (casse et espaces ignorés) ; `null` si ce n'est pas C1 à C4. */
export const parseLevel = (value: unknown): ConfidentialityLevel | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  return normalized === 'C1' || normalized === 'C2' || normalized === 'C3' || normalized === 'C4'
    ? normalized
    : null;
};

/** Rang d'un niveau (C1 = 1 … C4 = 4) ; un niveau inconnu vaut le défaut C2. */
export const levelRank = (level: unknown): number =>
  LEVEL_RANK[parseLevel(level) ?? DEFAULT_CONFIDENTIALITY_LEVEL];

/** Le plus restreint des deux niveaux. */
export const maxLevel = (
  left: ConfidentialityLevel,
  right: ConfidentialityLevel,
): ConfidentialityLevel => (levelRank(left) >= levelRank(right) ? left : right);

/** Vrai si `candidate` est strictement plus ouvert que `baseline` (une baisse, refusée par le serveur). */
export const isLowerLevel = (candidate: unknown, baseline: unknown): boolean =>
  levelRank(candidate) < levelRank(baseline);

/** Normalise un code de compartiment (`' sante '` -> `SANTE`). */
export const normalizeCompartment = (code: unknown): string =>
  typeof code === 'string' ? code.trim().toUpperCase() : '';

/** Liste de compartiments propre : majuscules, sans vide ni doublon, ordre d'origine. */
export const normalizeCompartments = (codes: readonly unknown[] | null | undefined): string[] => {
  if (!Array.isArray(codes)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  codes.forEach((code) => {
    const normalized = normalizeCompartment(code);
    if (normalized !== '' && !seen.has(normalized)) {
      seen.add(normalized);
      out.push(normalized);
    }
  });
  return out;
};

/** Vrai si les deux listes de compartiments portent les mêmes codes (ordre ignoré). */
export const sameCompartments = (
  left: readonly unknown[] | null | undefined,
  right: readonly unknown[] | null | undefined,
): boolean => {
  const a = normalizeCompartments(left);
  const b = new Set(normalizeCompartments(right));
  return a.length === b.size && a.every((code) => b.has(code));
};

// ─── Clés i18n et tons ───────────────────────────────────────────────────

/** Libellé d'un niveau (« Public », « Interne », « Confidentiel », « Secret »). */
export const levelLabelKey = (level: unknown): string => {
  switch (parseLevel(level)) {
    case 'C1':
      return 'core.classification.level.c1';
    case 'C3':
      return 'core.classification.level.c3';
    case 'C4':
      return 'core.classification.level.c4';
    default:
      return 'core.classification.level.c2';
  }
};

/** Description d'un niveau (info-bulle de l'administration). */
export const levelHintKey = (level: unknown): string => {
  switch (parseLevel(level)) {
    case 'C1':
      return 'core.classification.level.c1.hint';
    case 'C3':
      return 'core.classification.level.c3.hint';
    case 'C4':
      return 'core.classification.level.c4.hint';
    default:
      return 'core.classification.level.c2.hint';
  }
};

/**
 * Libellé d'un compartiment connu ; `null` pour un code hors du vocabulaire : l'appelant
 * se rabat alors sur le `label_i18n` servi par le serveur, puis sur le code lui-même.
 */
export const compartmentLabelKey = (code: unknown): string | null => {
  switch (normalizeCompartment(code)) {
    case 'PII':
      return 'core.classification.compartment.pii';
    case 'HR':
      return 'core.classification.compartment.hr';
    case 'FIN':
      return 'core.classification.compartment.fin';
    case 'CRISE':
      return 'core.classification.compartment.crise';
    case 'SANTE':
      return 'core.classification.compartment.sante';
    case 'LEGAL':
      return 'core.classification.compartment.legal';
    default:
      return null;
  }
};

/**
 * Ton d'état d'un niveau : C1 et C2 neutres, C3 avertissement, C4 danger. Une couleur
 * d'état n'emprunte jamais la marque du locataire (règle état vs marque, 18.09).
 */
export const levelTone = (level: unknown): ClassificationTone => {
  switch (parseLevel(level)) {
    case 'C3':
      return 'warning';
    case 'C4':
      return 'danger';
    default:
      return 'neutral';
  }
};

/**
 * Vrai si un champ de cette classification porte la CLEF dans un formulaire ou une liste (UAT 05/10,
 * UPT-2051) : C3 (confidentiel) et C4 (secret), pour TOUT utilisateur, habilité ou non. C1, C2 et une
 * classification absente ou illisible n'ont rien.
 */
export const hasProtectedKey = (classification: ClassificationLike | null | undefined): boolean =>
  parseLevel(classification?.level) !== null && levelRank(classification?.level) >= levelRank('C3');

/** Séparateur du niveau et des compartiments dans une info-bulle : « C3 · PII · SANTE ». */
export const CLASSIFICATION_DETAIL_SEPARATOR = ' · ';

/**
 * Détail d'une classification pour une info-bulle : le niveau puis les compartiments (« C3 · PII · SANTE »).
 * `labelOf` remplace chaque code de compartiment par son libellé (lecteur d'écran) ; sans lui, les codes.
 */
export const classificationDetail = (
  classification: ClassificationLike | null | undefined,
  labelOf?: (code: string) => string,
): string => {
  const level = parseLevel(classification?.level);
  const codes = normalizeCompartments(classification?.compartments).map((code) => (labelOf ? labelOf(code) : code));
  return [level ?? '', ...codes].filter((part) => part !== '').join(CLASSIFICATION_DETAIL_SEPARATOR);
};

/** Portée dont vient la classification d'un champ (colonne « Hérité de »). */
export const inheritedLabelKey = (inherited: unknown): string => {
  switch (inherited) {
    case 'field':
      return 'core.classification.inherited.field';
    case 'section':
      return 'core.classification.inherited.section';
    case 'entity':
      return 'core.classification.inherited.entity';
    default:
      return 'core.classification.inherited.default';
  }
};

/**
 * Codes d'erreur métier de `/api/v1/classification` (et du caviardage, phase 1) que le web
 * traduit. Les 422 portent un `code` stable ; `details.fields` liste les champs fautifs.
 */
export const CLASSIFICATION_ERROR_CODES = [
  'classification.cannotLower',
  'classification.requiresTokenization',
  'classification.fieldReadOnly',
  'classification.denied',
  'classification.unknownCompartment',
  'classification.invalidLevel',
  'classification.invalidScope',
  'classification.c4Reserved',
  'classification.compartmentReserved',
  'classification.reasonRequired',
  'classification.invalidDuration',
  'classification.userNotInTenant',
  'classification.keyCeilingC4Forbidden',
] as const;

export type ClassificationErrorCode = (typeof CLASSIFICATION_ERROR_CODES)[number];

/** Clé i18n du message d'un code d'erreur ; `null` pour un code que le web ne connaît pas. */
export const classificationErrorMessageKey = (code: unknown): string | null => {
  switch (code) {
    case 'classification.cannotLower':
      return 'core.classification.error.cannotLower';
    case 'classification.requiresTokenization':
      return 'core.classification.error.requiresTokenization';
    case 'classification.fieldReadOnly':
      return 'core.classification.error.fieldReadOnly';
    case 'classification.denied':
      return 'core.classification.error.denied';
    case 'classification.unknownCompartment':
      return 'core.classification.error.unknownCompartment';
    case 'classification.invalidLevel':
      return 'core.classification.error.invalidLevel';
    case 'classification.invalidScope':
      return 'core.classification.error.invalidScope';
    case 'classification.c4Reserved':
      return 'core.classification.error.c4Reserved';
    case 'classification.compartmentReserved':
      return 'core.classification.error.compartmentReserved';
    case 'classification.reasonRequired':
      return 'core.classification.error.reasonRequired';
    case 'classification.invalidDuration':
      return 'core.classification.error.invalidDuration';
    case 'classification.userNotInTenant':
      return 'core.classification.error.userNotInTenant';
    case 'classification.keyCeilingC4Forbidden':
      return 'core.classification.error.keyCeilingC4Forbidden';
    default:
      return null;
  }
};

// ─── Lecture anticipée (affichage seulement) ─────────────────────────────

/**
 * Vrai si `clearance` couvre `classification` : niveau au moins égal ET compartiments du
 * champ inclus dans ceux de l'habilitation.
 *
 * AFFICHAGE ANTICIPÉ SEULEMENT (badge « vous ne verrez pas ce champ ») : le serveur décide
 * à chaque réponse. Deux replis volontaires, pour ne jamais prétendre qu'un champ est
 * caché sans certitude :
 *   - habilitation inconnue (contexte absent ou serveur antérieur à la phase 0) : `true` ;
 *   - classification absente : le défaut du serveur, C2 sans compartiment.
 */
export const canReadWithClearance = (
  classification: ClassificationLike | null | undefined,
  clearance: Pick<Clearance, 'level' | 'compartments'> | null | undefined,
): boolean => {
  if (!clearance || parseLevel(clearance.level) === null) return true;
  const required = parseLevel(classification?.level) ?? DEFAULT_CONFIDENTIALITY_LEVEL;
  if (levelRank(clearance.level) < levelRank(required)) return false;
  const held = new Set(normalizeCompartments(clearance.compartments));
  return normalizeCompartments(classification?.compartments).every((code) => held.has(code));
};

// ─── Caviardage (lecture de `_classification`) ───────────────────────────

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Champs caviardés d'un objet d'entité (`record._classification.redacted`), copie défensive.
 * Vide quand le serveur ne caviarde rien (ou, avant la phase 1, ne dit rien).
 */
export const redactedFields = (record: unknown): string[] => {
  if (!isRecord(record)) return [];
  const meta = record._classification;
  if (!isRecord(meta) || !Array.isArray(meta.redacted)) return [];
  return meta.redacted.filter((field): field is string => typeof field === 'string' && field !== '');
};

/** Vrai si le serveur déclare `field` caviardé dans `record` (casse du dictionnaire d'abord, puis insensible). */
export const isRedacted = (record: unknown, field: string): boolean => {
  if (typeof field !== 'string' || field === '') return false;
  const fields = redactedFields(record);
  if (fields.includes(field)) return true;
  const wanted = field.toLowerCase();
  return fields.some((candidate) => candidate.toLowerCase() === wanted);
};

/** Niveau déclaré par le serveur pour un champ caviardé (`_classification.levels`), `null` si absent. */
export const redactedLevel = (record: unknown, field: string): ConfidentialityLevel | null => {
  if (!isRecord(record) || !isRecord(record._classification)) return null;
  const levels = record._classification.levels;
  if (!isRecord(levels)) return null;
  const direct = parseLevel(levels[field]);
  if (direct !== null) return direct;
  const wanted = field.toLowerCase();
  const key = Object.keys(levels).find((candidate) => candidate.toLowerCase() === wanted);
  return key === undefined ? null : parseLevel(levels[key]);
};

/**
 * Lit les libellés d'un compartiment servis par le serveur (`label_i18n` : fr, en, de, it,
 * ar) dans la langue de l'interface ; repli sur l'anglais, le français, puis le premier texte.
 */
export const compartmentServerLabel = (
  labelI18n: unknown,
  locale: string | null | undefined,
): string | null => {
  if (!isRecord(labelI18n)) return null;
  const pick = (lang: string): string | null => {
    const value = labelI18n[lang];
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  };
  const language = (locale ?? '').toLowerCase().split(/[-_]/)[0];
  return (
    (language !== '' ? pick(language) : null) ??
    pick('en') ??
    pick('fr') ??
    Object.values(labelI18n).find((value): value is string => typeof value === 'string' && value.trim() !== '') ??
    null
  );
};

/**
 * Libellé affiché d'un compartiment : la traduction du code connu (cinq langues), sinon le
 * `label_i18n` servi par le serveur dans la langue de l'interface, sinon le code lui-même.
 * `translate` est le `t(id)` du composant (jamais de clé composée dans le JSX).
 */
export const compartmentDisplayLabel = (
  code: unknown,
  translate: (id: string) => string,
  serverLabelI18n?: unknown,
  locale?: string | null,
): string => {
  const normalized = normalizeCompartment(code);
  const key = compartmentLabelKey(normalized);
  if (key !== null) return translate(key);
  return compartmentServerLabel(serverLabelI18n, locale) ?? normalized;
};

// ─── Erreurs : message traduit, champs fautifs lisibles ──────────────────

export type ClassificationTranslate = (id: string, values?: Record<string, string | number>) => string;

export type ClassificationErrorInfo = {
  /** Message traduit, prêt à afficher (calculé hors du JSX : règle i18n 4a). */
  message: string;
  /** Code stable du serveur (`classification.cannotLower`…), `null` si l'erreur n'en porte pas. */
  code: string | null;
  status: number | null;
  /** Nœuds fautifs tels que le serveur les nomme : clé du champ, `section:<nom>` ou `(entity)`. */
  fields: string[];
};

/**
 * Nom lisible d'un nœud nommé par le serveur dans `details.fields` : la clé du champ telle
 * quelle, `section:<nom>` -> section « <nom> », `(entity)` -> plancher de l'entité.
 */
export const classificationNodeLabel = (node: string, translate: ClassificationTranslate): string => {
  if (node === '(entity)') return translate('core.classification.scope.entity');
  if (node.startsWith('section:')) {
    return translate('core.classification.scope.section', { name: node.slice('section:'.length) });
  }
  return node;
};

/** Ce qu'une erreur d'API dit de la classification : code stable, statut HTTP, nœuds fautifs. */
export type ParsedClassificationError = {
  /** Code `classification.*` du serveur, `null` si l'erreur n'en porte pas (ou si le code est d'un autre domaine). */
  code: string | null;
  status: number | null;
  fields: string[];
};

/**
 * Lit une erreur d'API (`ClassificationApiError`, `AuditApiError`… ou erreur HTTP brute d'axios) :
 * `code` / `status` / `fields` directs, sinon `response.data.code`, `response.status` et
 * `response.data.details.fields`. Ne traduit rien : le texte du serveur n'est jamais retenu.
 */
export const parseClassificationError = (error: unknown): ParsedClassificationError => {
  const source = isRecord(error) ? error : {};
  const response = isRecord(source.response) ? source.response : null;
  const body = response && isRecord(response.data) ? response.data : null;
  const rawCode =
    typeof source.code === 'string' ? source.code : body && typeof body.code === 'string' ? body.code : null;
  const code = rawCode !== null && rawCode.startsWith('classification.') ? rawCode : null;
  const status =
    typeof source.status === 'number'
      ? source.status
      : response && typeof response.status === 'number'
        ? response.status
        : null;
  const rawFields = Array.isArray(source.fields)
    ? source.fields
    : body && isRecord(body.details) && Array.isArray(body.details.fields)
      ? body.details.fields
      : [];
  const fields = rawFields.filter((field): field is string => typeof field === 'string' && field !== '');
  return { code, status, fields };
};

/**
 * Erreur d'un appel de classification (`ClassificationApiError` ou erreur HTTP brute) -> message
 * traduit. Le code stable du serveur décide ; sans code, un 403 dit « droits insuffisants » et le
 * reste « échec de la requête ». Le texte du serveur n'est jamais affiché tel quel.
 */
export const describeClassificationError = (
  error: unknown,
  translate: ClassificationTranslate,
): ClassificationErrorInfo => {
  const { code, status, fields } = parseClassificationError(error);
  const key = classificationErrorMessageKey(code);
  let message: string;
  if (key !== null) {
    // `count` permet aux messages de n'afficher « Concerné : … » que s'il y a des champs fautifs (ICU plural).
    message = translate(key, {
      count: fields.length,
      fields: fields.map((field) => classificationNodeLabel(field, translate)).join(', '),
    });
  } else if (status === 403) {
    message = translate('core.classification.error.forbidden');
  } else {
    message = translate('core.classification.error.generic');
  }
  return { message, code, status, fields };
};
